import React, { StrictMode, Suspense, lazy } from 'react';
import { render as DOMrender } from 'react-dom';
import { injectRouter } from './interface/route';
import { injectConfigRoute } from './interface/config';
import { injectServiceWorker } from './interface/serviceworker';
import { injectENV, injectLog } from './interface/util';
import { IContainer } from './interface/container';
import type { ReportHandler } from 'web-vitals';

import { bind as localBindConfig } from './config/bind';
import { bind as localBindRouter } from './routes/bind';
import { bind as localBindWorker } from './worker/bind';
import { bind as apiServiceBind } from '@monofe/apiservice';
import { bindAllTo as bindUtil, bindContainer, buildContainer } from '@monofe/util';
const bindMediaPlayer = () =>
    Promise.resolve(); /* placeholder, including path results in files bundled into static/js/main*.js increasing initial bundle size */

const ID_ROOT_ELEMENT = 'root';
const LOADING_OUTER_CLASSNAME = 'OnBootCenterModal OnBootLoadingStyle';
const LOADING_INNER_CLASSNAME = 'OnBootSpinner';
const ENV_IS_SW_ENABLED = 'REACT_APP_SW_ENABLED';
const ENV_SW_OVERRIDE = 'REACT_APP_SWFORCE';
const ENV_BUILDTYPE = 'NODE_ENV';

function LoadingView(): JSX.Element {
    return (
        <div className={LOADING_OUTER_CLASSNAME}>
            <div className={LOADING_INNER_CLASSNAME}></div>
        </div>
    );
}

function loadContainerBindings(container: IContainer): Promise<void> {
    return new Promise(async function (resolve, reject) {
        try {
            const bindApiService = (container: IContainer) => {
                apiServiceBind(container);
                return Promise.resolve();
            };

            const binders: Array<(container: IContainer) => Promise<void>> = [
                bindUtil,
                localBindConfig,
                bindApiService,
                localBindRouter,
                localBindWorker,
                bindMediaPlayer,
            ];

            for (const binder of binders) {
                await binder(container);
            }

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

function startServiceWorker(container: IContainer): Promise<void> {
    return new Promise(async function (resolve, reject) {
        try {
            const env = injectENV(container);
            const isProduction = env.getString(ENV_BUILDTYPE) === 'production';
            /* for testing, run `yarn startsw` to force the Service Worker to run in development */
            const swEnableOverride = env.getBoolean(ENV_SW_OVERRIDE);
            const hasServiceWorker = 'serviceWorker' in navigator;
            const swEnabled = env.getBoolean(
                ENV_IS_SW_ENABLED,
                true,
            ); /* if missing assume YES, otherwise allow for ENV to disable service-workers (Agnelli) */

            if (!swEnabled) {
            } else if (!hasServiceWorker) {
            } else if (isProduction || swEnableOverride) {
                const sw = await injectServiceWorker(container);
                sw.start();
            }

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

function startRenderer(container: IContainer): Promise<void> {
    return new Promise(async function (resolve, reject) {
        try {
            const router = injectRouter(container);
            const config = injectConfigRoute(container);

            const allRoutes = router.allRoutes();

            const App = lazy(() => import('./app'));

            DOMrender(
                <StrictMode>
                    <Suspense fallback={LoadingView}>
                        <App basename={config.basename} routes={allRoutes} />
                    </Suspense>
                </StrictMode>,
                document.getElementById(ID_ROOT_ELEMENT),
            );

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

/* start */
const container = buildContainer();
bindContainer(container);

loadContainerBindings(container)
    .then(_ => startServiceWorker(container))
    .then(_ => startRenderer(container))
    .then(_ => {
        injectLog(container).milestone('App running');
    })
    .catch(error => {
        injectLog(container).error(`Failed to start app: ${[error]}`);
    });

/* If you want to start measuring performance in your app, pass a function
   to log results (for example: reportWebVitals(console.log))
   or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals */
const isProduction = process?.env?.NODE_ENV === 'production'; /* disabled for production builds */
if (!isProduction) {
    const reportWebVitals = (onPerfEntry?: ReportHandler) => {
        if (onPerfEntry && onPerfEntry instanceof Function) {
            import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
                getCLS(onPerfEntry);
                getFID(onPerfEntry);
                getFCP(onPerfEntry);
                getLCP(onPerfEntry);
                getTTFB(onPerfEntry);
            });
        }
    };

    reportWebVitals();
}
